/**
 * Cryptic AgeGenderNet session traits (gn / gn_confidence / ag).
 *
 * Persisted in sessionStorage once known so every analytics event in the
 * browser session can segment by gender/age — Face AI funnel, entry, A/B, etc.
 * Wire keys stay opaque (never age/gender). No ag_confidence (net has none).
 */

export type FaceAiDemoSignals = {
  gn: "m" | "f";
  gn_confidence?: number;
  ag?: number;
};

const DG_SESSION_KEY = "face_ai_dg_v1";

function round2(n: number) {
  return Math.round(n * 100) / 100;
}

/** Normalize from WASM report / handoff / partial object. */
export function pickFaceAiDemoSignals(
  source: unknown
): FaceAiDemoSignals | null {
  if (!source || typeof source !== "object") return null;
  const raw = source as Record<string, unknown>;
  const gn = raw.gn;
  if (gn !== "m" && gn !== "f") return null;
  const out: FaceAiDemoSignals = { gn };
  const gp = Number(raw.gn_confidence);
  if (Number.isFinite(gp) && gp >= 0 && gp <= 1) {
    out.gn_confidence = round2(gp);
  }
  const age = Number(raw.ag);
  if (Number.isFinite(age) && age >= 0 && age <= 120) {
    out.ag = Math.round(age);
  }
  return out;
}

export function readFaceAiDemoSignals(): FaceAiDemoSignals | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(DG_SESSION_KEY);
    if (!raw) return null;
    return pickFaceAiDemoSignals(JSON.parse(raw));
  } catch {
    return null;
  }
}

/**
 * Persist session traits. Pass null to clear.
 * Returns the stored payload (or null).
 */
export function writeFaceAiDemoSignals(
  source: unknown
): FaceAiDemoSignals | null {
  if (typeof window === "undefined") return null;
  if (source == null) {
    try {
      sessionStorage.removeItem(DG_SESSION_KEY);
    } catch {
      /* ignore */
    }
    return null;
  }
  const signals = pickFaceAiDemoSignals(source);
  if (!signals) return readFaceAiDemoSignals();
  try {
    sessionStorage.setItem(DG_SESSION_KEY, JSON.stringify(signals));
  } catch {
    /* quota / private mode */
  }
  return signals;
}

/**
 * Always-present property bag for analytics payloads.
 * Missing traits → null so dashboards can filter "unknown".
 */
export function faceAiDemoSignalProps(
  override?: FaceAiDemoSignals | null
): {
  gn: "m" | "f" | null;
  gn_confidence: number | null;
  ag: number | null;
} {
  const s = override ?? readFaceAiDemoSignals();
  return {
    gn: s?.gn ?? null,
    gn_confidence:
      s && Number.isFinite(Number(s.gn_confidence))
        ? Number(s.gn_confidence)
        : null,
    ag: s && Number.isFinite(Number(s.ag)) ? Math.round(Number(s.ag)) : null,
  };
}
